/* assets/jintcm-wellness-check.js — JinTCM wellness self-check state machine
 *
 * Top-level pure functions (isSelectable here; isAnswered/visibleQuestions/isStepComplete
 * added in a later task) are Node-unit-testable — see tests/jintcm-wellness-check.test.cjs.
 * The IIFE below is the DOM-driven state machine itself; it's guarded to only run in a
 * browser, so requiring this file in Node exercises just the pure helpers.
 */

function isSelectable(catId, availableCategories) {
  return availableCategories.length === 0 || availableCategories.includes(catId);
}

function isAnswered(a) {
  return Array.isArray(a) ? a.length > 0 : typeof a === 'number';
}

function visibleQuestions(qs, catAns) {
  return qs.filter(function (q) {
    if (!q.showIf) return true;
    var priorAnswer = catAns[q.showIf.qi];
    var priorSelections = Array.isArray(priorAnswer) ? priorAnswer : (typeof priorAnswer === 'number' ? [priorAnswer] : []);
    return q.showIf.oi.some(function (oi) { return priorSelections.includes(oi); });
  });
}

function isStepComplete(qs, catAns) {
  var visible = visibleQuestions(qs, catAns);
  return visible.length > 0 && visible.every(function (q) { return isAnswered(catAns[qs.indexOf(q)]); });
}

function optionText(opt) {
  return typeof opt === 'string' ? opt : opt.text;
}

function isExclusiveOption(opt) {
  return typeof opt === 'object' && opt !== null && opt.exclusive === true;
}

function applyExclusiveToggle(existingSelections, opts, clickedOi) {
  const alreadySelected = existingSelections.includes(clickedOi);
  if (isExclusiveOption(opts[clickedOi])) {
    return alreadySelected ? [] : [clickedOi];
  }
  const withoutExclusives = existingSelections.filter((oi) => !isExclusiveOption(opts[oi]));
  return alreadySelected
    ? withoutExclusives.filter((oi) => oi !== clickedOi)
    : [...withoutExclusives, clickedOi];
}

// Design system v2: category labels show the Chinese name only; pinyin is not displayed
// (herb/product pinyin names are unaffected).
function categoryZhLabel(cat) {
  return cat.zh;
}

if (typeof module === 'object' && module.exports) {
  module.exports = { isSelectable: isSelectable, isAnswered: isAnswered, visibleQuestions: visibleQuestions, isStepComplete: isStepComplete, optionText: optionText, applyExclusiveToggle: applyExclusiveToggle, categoryZhLabel: categoryZhLabel };
}

if (typeof document !== 'undefined') {
(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────
  const LS = {
    intent:     'jintcm_intent',
    profile:    'jintcm_profile',
    priorities: 'jintcm_priorities',
    answers:    'jintcm_answers',
    conditions: 'jintcm_conditions',
  };
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const RESULTS_URL = ROOT + 'pages/your-results';
  const MAX_PRIORITIES = 3;
  const TOTAL_STEPS = 5;

  // Steps: 0=intent, 1=profile, 2=priorities, 3=symptoms, 4=conditions
  let currentStep = 0;
  let symptomIndex = 0;

  // ── State ──────────────────────────────────────────────────
  const state = {
    profile:    JSON.parse(localStorage.getItem(LS.profile)  || 'null') || { gender: null, ageGroup: null },
    priorities: JSON.parse(localStorage.getItem(LS.priorities) || '[]'),
    answers:    JSON.parse(localStorage.getItem(LS.answers)   || '{}'),
    conditions: JSON.parse(localStorage.getItem(LS.conditions) || '[]'),
  };

  function saveState() {
    localStorage.setItem(LS.profile,    JSON.stringify(state.profile));
    localStorage.setItem(LS.priorities, JSON.stringify(state.priorities));
    localStorage.setItem(LS.answers,    JSON.stringify(state.answers));
    localStorage.setItem(LS.conditions, JSON.stringify(state.conditions));
  }

  // ── Data ───────────────────────────────────────────────────
  const CATEGORIES  = JSON.parse(document.getElementById('jtcm-categories-data').textContent);
  const QUESTIONS   = JSON.parse(document.getElementById('jtcm-questions-data').textContent);
  const CONDITIONS  = JSON.parse(document.getElementById('jtcm-conditions-data').textContent);

  const INTENTS = [
    { id: 'wellness',  en: 'General wellness',       zh: '日常养生',  badge: 'Daily ritual',    cta: 'Continue',
      blurb: 'Daily maintenance — teas and custom blends to sustain your baseline.',
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 22V12M12 12C12 7 8 4 3 4c0 5 3 8 9 8zM12 12c0-5 4-8 9-8-1 5-4 8-9 8z"/></svg>' },
    { id: 'subhealth', en: 'Sub-health conditions',  zh: '亚健康调理', badge: 'Pattern care',   cta: 'Continue',
      blurb: 'Mild recurring patterns — we add OTC oral medicines to the mix.',
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 3l1.5 4.5H18l-3.75 2.75L15.75 15 12 12.25 8.25 15l1.5-4.75L6 7.5h4.5L12 3z"/></svg>' },
    { id: 'unwell',    en: 'I feel unwell today',    zh: '对症调理',  badge: 'Today',           cta: 'Continue',
      blurb: 'Symptomatic relief today — showing oral and external options.',
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="7" y="3" width="10" height="18" rx="2"/><line x1="10" y1="8" x2="14" y2="8"/><line x1="12" y1="6" x2="12" y2="10"/><line x1="10" y1="15" x2="14" y2="15"/></svg>' },
    { id: 'selfcheck', en: 'Try the self-assessment', zh: '自我评估', badge: 'Full self-check', cta: 'Begin the self-check',
      blurb: 'See everything and decide for yourself — no filter applied.',
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' },
  ];

  const GENDERS   = [
    { id: 'woman', en: 'Woman',           zh: '女' },
    { id: 'man',   en: 'Man',             zh: '男' },
    { id: 'other', en: 'Prefer not to say', zh: '不透露' },
  ];
  // Focus areas hidden from the Priorities step for a given gender — silent, no
  // re-ordering (parking-lot item 5). Genders with no entry here (e.g. 'other',
  // or unset) show all categories.
  const GENDER_HIDDEN_CATEGORIES = { man: ['gynecology'], woman: ['gentlemen'] };
  // Health Background conditions hidden from Step 5 for a given gender — silent, no
  // re-ordering (parking-lot item 5 follow-up). Unidirectional: only 'man' hides
  // anything here, unlike GENDER_HIDDEN_CATEGORIES above.
  const GENDER_HIDDEN_CONDITIONS = { man: ['pregnancy', 'breastfeeding'] };
  const AGE_GROUPS = [
    { id: '18-29', en: '18–29', zh: '18–29岁' },
    { id: '30-44', en: '30–44', zh: '30–44岁' },
    { id: '45-59', en: '45–59', zh: '45–59岁' },
    { id: '60+',   en: '60+',   zh: '60岁以上' },
  ];

  // ── DOM refs ───────────────────────────────────────────────
  const progressFill = document.getElementById('jtcm-progress-fill');
  const stepLabel    = document.getElementById('jtcm-step-label');
  const backBtn      = document.getElementById('jtcm-back-btn');
  const containers   = {
    intent:     document.getElementById('jtcm-step-intent'),
    profile:    document.getElementById('jtcm-step-profile'),
    priorities: document.getElementById('jtcm-step-priorities'),
    symptoms:   document.getElementById('jtcm-step-symptoms'),
    conditions: document.getElementById('jtcm-step-conditions'),
  };
  const emailGate = document.getElementById('jtcm-email-gate');
  const AVAILABLE_CATEGORIES = (document.getElementById('jtcm-quiz').dataset.availableCategories || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  // A non-empty allowlist skips Welcome/intent entirely, so every eyebrow step number
  // and the progress-bar step label shift down by one to stay in sync.
  const STEP_OFFSET = AVAILABLE_CATEGORIES.length > 0 ? 1 : 0;
  function eyebrowNum(n) { return String(n - STEP_OFFSET).padStart(2, '0'); }

  // ── Navigation ─────────────────────────────────────────────
  function goTo(step, symIdx) {
    currentStep  = step;
    symptomIndex = symIdx ?? 0;
    updateProgress();
    render();
  }

  function goNext() {
    if (currentStep === 1) { if (!profileReady()) return; goTo(2); return; }
    if (currentStep === 2) { if (!state.priorities.length) return; goTo(3); return; }
    if (currentStep === 3) {
      const prio = state.priorities[symptomIndex];
      const qs   = QUESTIONS[prio] || [];
      const ans  = state.answers[prio] || {};
      if (!isStepComplete(qs, ans)) return;
      if (symptomIndex < state.priorities.length - 1) { symptomIndex++; updateProgress(); render(); return; }
      goTo(4); return;
    }
    if (currentStep === 4) {
      if (!state.conditions.length) return;
      saveState();
      showEmailGate();
    }
  }

  function goBack() {
    if (currentStep === 0) return; // no back from step 1 (intent)
    if (currentStep === 1) { if (AVAILABLE_CATEGORIES.length > 0) return; goTo(0); return; }
    if (currentStep === 3 && symptomIndex > 0) { symptomIndex--; updateProgress(); render(); return; }
    if (currentStep === 3) { goTo(2); return; }
    goTo(currentStep - 1);
  }

  backBtn.addEventListener('click', goBack);

  function updateProgress() {
    const total = TOTAL_STEPS - STEP_OFFSET;
    let fraction;
    if (currentStep < 3) fraction = (currentStep + 1 - STEP_OFFSET) / total;
    else if (currentStep === 3) fraction = ((3 - STEP_OFFSET) + (symptomIndex + 1) / state.priorities.length) / total;
    else fraction = 1;
    progressFill.style.width = (fraction * 100) + '%';
    const displayStep = currentStep < 3 ? currentStep + 1 - STEP_OFFSET
                      : currentStep === 3 ? 4 - STEP_OFFSET
                      : 5 - STEP_OFFSET;
    stepLabel.textContent = 'Step 0' + displayStep + ' / 0' + total;
    // In a locked flow, Profile (step 1) is the first step — no back target
    // (Welcome/intent doesn't exist in this flow), so hide the button there too.
    backBtn.style.display = (currentStep === 0 || (AVAILABLE_CATEGORIES.length > 0 && currentStep === 1)) ? 'none' : '';
  }

  // ── Intent (Step 0) ────────────────────────────────────────
  function renderIntent() {
    const el = containers.intent;
    el.innerHTML = `
      <div class="jtcm-eyebrow" style="margin-bottom:1rem">01 — Your intent <span class="jtcm-eyebrow-line"></span> <span style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);letter-spacing:0;color:var(--jtcm-muted);text-transform:none">欢迎</span></div>
      <h2 class="jtcm-h2" style="color:var(--jtcm-ink);margin-bottom:.5rem">How can we <em style="color:var(--jtcm-sage-deep)">help you today?</em></h2>
      <p style="color:var(--jtcm-muted);font-size:var(--jtcm-fs-body-sm);line-height:1.6;margin-bottom:2rem">Pick the option that fits where you are. You can change your mind at any point.</p>
      <div class="jtcm-grid-2" id="jtcm-intent-grid"></div>
      <div style="margin-top:2rem;padding-top:1.25rem;border-top:1px solid var(--jtcm-sage-soft);display:flex;flex-direction:column;gap:1rem">
        <p style="font-size:var(--jtcm-fs-body-sm);color:var(--jtcm-muted);line-height:1.6;max-width:36rem">
          The recommendations shown are wellness suggestions, not prescriptions. If you take prescription medication, are pregnant, breastfeeding, or have a chronic condition, message a physician on WhatsApp before starting any new herbal product.
        </p>
        <div>
          <a href="https://wa.me/6588963210?text=Hi%20Jin%20TCM%2C%20I%20have%20a%20question%20before%20taking%20the%20wellness%20self-check."
             target="_blank" rel="noopener noreferrer" class="jtcm-btn jtcm-btn-outline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.855L.057 23.117a.75.75 0 0 0 .917.902l5.42-1.424A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.853 0-3.587-.5-5.082-1.373l-.364-.214-3.768.99.999-3.685-.234-.381A9.953 9.953 0 0 1 2 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
            Ask a physician
          </a>
        </div>
      </div>`;

    const grid = el.querySelector('#jtcm-intent-grid');
    INTENTS.forEach((intent) => {
      const btn = document.createElement('button');
      btn.className = 'jtcm-card jtcm-intent-card';
      btn.style.cssText = 'text-align:left;width:100%';
      btn.innerHTML = `
        <div style="display:flex;align-items:flex-start;gap:1rem">
          <div style="width:2.5rem;height:2.5rem;border-radius:9999px;background:var(--jtcm-cream,#F1ECE0);display:flex;align-items:center;justify-content:center;color:var(--jtcm-sage-deep,#34433A);flex-shrink:0">${intent.icon}</div>
          <div style="flex:1;min-width:0">
            <div style="font-size:var(--jtcm-fs-label);font-weight:600;letter-spacing:var(--jtcm-tracking-label);text-transform:uppercase;color:var(--jtcm-muted,#7C7868);margin-bottom:.4rem">${intent.badge}</div>
            <div style="font-family:var(--jtcm-font-serif-text);font-size:1.5rem;line-height:1.1;color:var(--jtcm-ink,#1A1813)">${intent.en}</div>
            <div style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-body-sm);color:var(--jtcm-sage-deep,#34433A);margin-top:.35rem">${intent.zh}</div>
            <p style="margin-top:.65rem;font-size:var(--jtcm-fs-small);line-height:1.55;color:var(--jtcm-muted,#7C7868)">${intent.blurb}</p>
            <div style="margin-top:.875rem;font-size:var(--jtcm-fs-label);font-weight:600;letter-spacing:var(--jtcm-tracking-label);text-transform:uppercase;color:var(--jtcm-sage-deep,#34433A);display:inline-flex;align-items:center;gap:.35rem;border-bottom:1px solid var(--jtcm-sage-soft,#C7CEBA);padding-bottom:2px">
              ${intent.cta} &#8594;
            </div>
          </div>
        </div>`;
      btn.addEventListener('click', () => {
        localStorage.removeItem(LS.profile);
        localStorage.removeItem(LS.priorities);
        localStorage.removeItem(LS.answers);
        localStorage.removeItem(LS.conditions);
        state.profile    = { gender: null, ageGroup: null };
        state.priorities = [];
        state.answers    = {};
        state.conditions = [];
        localStorage.setItem(LS.intent, intent.id);
        goTo(1);
      });
      grid.appendChild(btn);
    });
  }

  // ── Profile (Step 1) ───────────────────────────────────────
  function profileReady() {
    return state.profile.gender && state.profile.ageGroup;
  }

  function renderProfile() {
    const el = containers.profile;
    el.innerHTML = `
      <div class="jtcm-eyebrow" style="margin-bottom:1rem">${eyebrowNum(2)} — Profile <span class="jtcm-eyebrow-line"></span> <span style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);letter-spacing:0;color:var(--jtcm-muted);text-transform:none">自我评估</span></div>
      <h2 class="jtcm-h2" style="color:var(--jtcm-ink);margin-bottom:.5rem">Let's begin with <em style="color:var(--jtcm-sage-deep)">who you are.</em></h2>
      <p style="color:var(--jtcm-muted);font-size:var(--jtcm-fs-body-sm);line-height:1.6;margin-bottom:2rem">A short profile helps us tailor your wellness picks. We never share or sell what you share here.</p>
      <div class="jtcm-profile-grids" id="prof-grids">
        <div>
          <div style="font-size:var(--jtcm-fs-label);font-weight:600;letter-spacing:var(--jtcm-tracking-label);text-transform:uppercase;color:var(--jtcm-muted);margin-bottom:.75rem">A. Gender · 性别</div>
          <div class="jtcm-grid-fixed-2" id="gender-grid"></div>
        </div>
        <div>
          <div style="font-size:var(--jtcm-fs-label);font-weight:600;letter-spacing:var(--jtcm-tracking-label);text-transform:uppercase;color:var(--jtcm-muted);margin-bottom:.75rem">B. Age group · 年龄段</div>
          <div class="jtcm-grid-fixed-2" id="age-grid"></div>
        </div>
      </div>
      <div style="margin-top:2rem;padding-top:1.25rem;border-top:1px solid var(--jtcm-sage-soft);display:flex;justify-content:flex-end">
        <button id="prof-next" class="jtcm-btn jtcm-btn-sage">Continue &#8594;</button>
      </div>`;

    const gGrid = el.querySelector('#gender-grid');
    GENDERS.forEach(g => {
      const btn = document.createElement('button');
      btn.className = 'jtcm-card' + (state.profile.gender === g.id ? ' sel' : '');
      btn.style.cssText = 'text-align:left;width:100%';
      btn.innerHTML = `<div style="font-family:var(--jtcm-font-serif-text);font-size:1.5rem;line-height:1.2">${g.en}</div><div class="jtcm-card__meta" style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);margin-top:.25rem">${g.zh}</div>`;
      btn.addEventListener('click', () => {
        state.profile.gender = g.id;
        document.querySelectorAll('#gender-grid .jtcm-card').forEach(b => b.classList.remove('sel'));
        btn.classList.add('sel');
        updateNextBtn();
      });
      gGrid.appendChild(btn);
    });

    const aGrid = el.querySelector('#age-grid');
    AGE_GROUPS.forEach(a => {
      const btn = document.createElement('button');
      btn.className = 'jtcm-card' + (state.profile.ageGroup === a.id ? ' sel' : '');
      btn.style.cssText = 'text-align:left;width:100%';
      btn.innerHTML = `<div style="font-family:var(--jtcm-font-serif-text);font-size:1.5rem;line-height:1.2">${a.en}</div><div class="jtcm-card__meta" style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);margin-top:.25rem">${a.zh}</div>`;
      btn.addEventListener('click', () => {
        state.profile.ageGroup = a.id;
        document.querySelectorAll('#age-grid .jtcm-card').forEach(b => b.classList.remove('sel'));
        btn.classList.add('sel');
        updateNextBtn();
      });
      aGrid.appendChild(btn);
    });

    function updateNextBtn() {
      el.querySelector('#prof-next').disabled = !profileReady();
    }
    updateNextBtn();
    el.querySelector('#prof-next').addEventListener('click', goNext);
  }

  // ── Priorities (Step 2) ────────────────────────────────────
  function renderPriorities() {
    const isRestricted = AVAILABLE_CATEGORIES.length > 0;
    const isSingleLocked = isRestricted && AVAILABLE_CATEGORIES.length === 1;
    const hiddenCats = GENDER_HIDDEN_CATEGORIES[state.profile.gender] || [];
    state.priorities = state.priorities.filter(id => !hiddenCats.includes(id));
    const visibleCategories = CATEGORIES.filter(cat => !hiddenCats.includes(cat.id));
    const el = containers.priorities;
    el.innerHTML = `
      <div class="jtcm-eyebrow" style="margin-bottom:1rem">${eyebrowNum(3)} — Priorities <span class="jtcm-eyebrow-line"></span> <span style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);letter-spacing:0;color:var(--jtcm-muted);text-transform:none">关注重点</span></div>
      <h2 class="jtcm-h2" style="color:var(--jtcm-ink);margin-bottom:.5rem">${isSingleLocked
        ? `This assessment focuses on <em style="color:var(--jtcm-sage-deep)">${(CATEGORIES.find(c => c.id === AVAILABLE_CATEGORIES[0]) || {}).en || ''}.</em>`
        : `Where shall we <em style="color:var(--jtcm-sage-deep)">focus your picks?</em>`}</h2>
      <p style="color:var(--jtcm-muted);font-size:var(--jtcm-fs-body-sm);line-height:1.6;margin-bottom:2rem">${isSingleLocked
        ? `More focus areas are coming soon — for now we're showing picks for ${(CATEGORIES.find(c => c.id === AVAILABLE_CATEGORIES[0]) || {}).en || 'this area'}.`
        : `Choose up to ${MAX_PRIORITIES} focus areas. <span id="prio-count">${state.priorities.length}/${MAX_PRIORITIES} selected.</span>`}</p>
      <div class="jtcm-grid-3" id="prio-grid"></div>
      <div style="margin-top:2rem;padding-top:1.25rem;border-top:1px solid var(--jtcm-sage-soft);display:flex;align-items:center;justify-content:space-between">
        <div style="font-size:var(--jtcm-fs-small);color:var(--jtcm-muted)">Selected: <strong id="prio-count-footer">${state.priorities.length || 'none'}</strong></div>
        <button id="prio-next" class="jtcm-btn jtcm-btn-sage" ${state.priorities.length === 0 ? 'disabled' : ''}>Continue &#8594;</button>
      </div>`;

    const grid = el.querySelector('#prio-grid');
    visibleCategories.forEach(cat => {
      const selectable = isSelectable(cat.id, AVAILABLE_CATEGORIES);
      const isSel = state.priorities.includes(cat.id);
      const btn = document.createElement('button');
      btn.className = 'jtcm-card jtcm-card--priority' + (isSel ? ' sel' : '') + (selectable ? '' : ' jtcm-card--coming-soon');
      btn.style.cssText = 'text-align:left;width:100%;display:flex;flex-direction:column;justify-content:space-between;position:relative';
      btn.disabled = !selectable;
      btn.innerHTML = `
        ${selectable ? '' : '<span class="jtcm-coming-soon-badge">Coming soon</span>'}
        <div>
          <div style="font-family:var(--jtcm-font-serif-text);font-size:1.375rem;line-height:1.15">${cat.en}</div>
          <div class="jtcm-card__meta--deep" style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);margin-top:.25rem">${categoryZhLabel(cat)}</div>
          <p class="jtcm-card__meta" style="font-size:var(--jtcm-fs-small);margin-top:.5rem;line-height:1.5">${cat.blurb}</p>
        </div>
        <div style="display:flex;align-items:center;justify-content:flex-end">
          <span class="jtcm-check ${isSel ? 'sel' : ''}">${selectable ? (isSel ? '✓' : '+') : ''}</span>
        </div>`;
      if (selectable && !isSingleLocked) {
        btn.addEventListener('click', () => {
          const i = state.priorities.indexOf(cat.id);
          if (i !== -1) state.priorities.splice(i, 1);
          else if (state.priorities.length < MAX_PRIORITIES) state.priorities.push(cat.id);
          else return; // at max
          refreshPriorities();
        });
      }
      grid.appendChild(btn);
    });

    function refreshPriorities() {
      grid.querySelectorAll('.jtcm-card').forEach((btn, bi) => {
        const cat = visibleCategories[bi];
        const selectable = isSelectable(cat.id, AVAILABLE_CATEGORIES);
        const sel = state.priorities.includes(cat.id);
        const idx = state.priorities.indexOf(cat.id);
        btn.classList.toggle('sel', sel);
        const chk = btn.querySelector('.jtcm-check');
        chk.classList.toggle('sel', sel);
        chk.textContent = selectable ? (sel ? '✓' : '+') : '';
      });
      const countEl = el.querySelector('#prio-count');
      if (countEl) countEl.textContent = `${state.priorities.length}/${MAX_PRIORITIES} selected.`;
      el.querySelector('#prio-count-footer').textContent = state.priorities.length || 'none';
      el.querySelector('#prio-next').disabled = state.priorities.length === 0;
    }

    el.querySelector('#prio-next').addEventListener('click', goNext);
  }

  // ── Symptoms (Step 3) ──────────────────────────────────────
  function renderSymptoms() {
    const el = containers.symptoms;
    const prio = state.priorities[symptomIndex];
    const cat  = CATEGORIES.find(c => c.id === prio);
    const qs   = QUESTIONS[prio] || [];
    const catAns = state.answers[prio] || {};
    const visible = visibleQuestions(qs, catAns);
    const answered = visible.filter((q) => isAnswered(catAns[qs.indexOf(q)])).length;
    const isLast = symptomIndex === state.priorities.length - 1;

    // Sub-progress dots
    const dots = state.priorities.map((_, i) =>
      `<span style="height:3px;flex:1;border-radius:2px;background:${i <= symptomIndex ? 'var(--jtcm-sage-deep)' : 'var(--jtcm-sage-soft)'}"></span>`
    ).join('');

    el.innerHTML = `
      <div class="jtcm-eyebrow" style="margin-bottom:1rem">${eyebrowNum(4)} — Day-to-day · Area ${symptomIndex + 1} of ${state.priorities.length} <span class="jtcm-eyebrow-line"></span> <span style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);letter-spacing:0;color:var(--jtcm-muted);text-transform:none">了解您</span></div>
      <div style="display:flex;gap:.4rem;margin-bottom:1.5rem">${dots}</div>
      <h2 class="jtcm-h2" style="color:var(--jtcm-ink);margin-bottom:.5rem">Tell us what your <em style="color:var(--jtcm-sage-deep)">body's been saying.</em></h2>
      <p style="color:var(--jtcm-muted);font-size:var(--jtcm-fs-body-sm);line-height:1.6;margin-bottom:2rem" id="sym-answered-line">${answered}/${visible.length} answered for ${cat ? cat.en : ''}</p>

      <div style="border:1px solid var(--jtcm-sage-soft);background:var(--jtcm-cream-warm);border-radius:var(--jtcm-radius-card);overflow:hidden">
        <div style="padding:1.25rem 1.5rem;border-bottom:1px solid var(--jtcm-sage-soft);display:flex;align-items:center;justify-content:space-between">
          <div>
            <div style="font-size:var(--jtcm-fs-label);font-weight:600;letter-spacing:var(--jtcm-tracking-label);text-transform:uppercase;color:var(--jtcm-muted)">Area ${symptomIndex + 1} of ${state.priorities.length}</div>
            <div style="font-family:var(--jtcm-font-serif-text);font-size:1.5rem;line-height:1.2">${cat ? cat.en : ''} <span style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);color:var(--jtcm-sage-deep)">· ${cat ? cat.zh : ''}</span></div>
          </div>
          <span id="sym-complete-badge" style="font-size:var(--jtcm-fs-label);font-weight:600;letter-spacing:var(--jtcm-tracking-label);text-transform:uppercase;color:var(--jtcm-sage-deep);${answered === visible.length ? '' : 'display:none'}">✓ Complete</span>
        </div>
        <div id="sym-questions"></div>
      </div>

      <div style="margin-top:2rem;padding-top:1.25rem;border-top:1px solid var(--jtcm-sage-soft);display:flex;align-items:center;justify-content:space-between">
        <div style="font-size:var(--jtcm-fs-small);color:var(--jtcm-muted)" id="sym-answered-footer">${answered} of ${visible.length} answered</div>
        <button id="sym-next" class="jtcm-btn jtcm-btn-sage" ${!isStepComplete(qs, catAns) ? 'disabled' : ''}>
          ${isLast ? 'Continue &#8594;' : 'Next area &#8594;'}
        </button>
      </div>`;

    const qContainer = el.querySelector('#sym-questions');
    // Render every question in the bank (not just currently-visible ones) as a persistent DOM
    // node, wrapped in a collapse container whose expanded/collapsed state is a CSS class — this
    // lets a later visibility change animate via CSS transition instead of the node being
    // destroyed and recreated on every click (which can't be transitioned).
    qs.forEach((q, qi) => {
      const collapseEl = document.createElement('div');
      collapseEl.className = 'jtcm-sym-collapse' + (visible.includes(q) ? ' jtcm-sym-collapse--expanded' : '');
      collapseEl.dataset.qi = qi;
      collapseEl.inert = !visible.includes(q);

      const qEl = document.createElement('div');
      qEl.className = 'jtcm-sym-row' + (qi > 0 ? ' jtcm-sym-row--divider' : '');

      const currentAns = catAns[qi];
      const selectedSet = Array.isArray(currentAns) ? currentAns : (typeof currentAns === 'number' ? [currentAns] : []);
      const optsHTML = q.opts.map((opt, oi) => {
        const isSel = selectedSet.includes(oi);
        const indicatorClass = q.multi ? 'jtcm-checkbox' : 'jtcm-radio';
        return `
        <button class="jtcm-card sym-opt ${isSel ? 'sel' : ''}"
                data-qi="${qi}" data-oi="${oi}"
                style="text-align:left;width:100%;display:flex;align-items:center;gap:.75rem;padding:.75rem 1rem">
          <span class="${indicatorClass} ${isSel ? 'sel' : ''}"></span>
          <span style="font-size:var(--jtcm-fs-body-sm);line-height:1.6">${optionText(opt)}</span>
        </button>`;
      }).join('');

      qEl.innerHTML = `
        <div>
          <div style="font-size:var(--jtcm-fs-label);font-weight:600;letter-spacing:var(--jtcm-tracking-label);text-transform:uppercase;color:var(--jtcm-muted);margin-bottom:.35rem">Q · 0${qi + 1}</div>
          <div style="font-family:var(--jtcm-font-serif-text);font-size:1.25rem;line-height:1.3">${q.q}</div>
          <div style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);color:var(--jtcm-sage-deep);margin-top:.2rem">${q.zh}</div>
        </div>
        <div class="jtcm-sym-opts">${optsHTML}</div>`;

      qEl.querySelectorAll('.sym-opt').forEach(btn => {
        btn.addEventListener('click', () => {
          const qi2 = +btn.dataset.qi, oi2 = +btn.dataset.oi;
          if (!state.answers[prio]) state.answers[prio] = {};
          if (q.multi) {
            const existing = Array.isArray(state.answers[prio][qi2]) ? state.answers[prio][qi2] : [];
            state.answers[prio][qi2] = applyExclusiveToggle(existing, q.opts, oi2);
          } else {
            state.answers[prio][qi2] = oi2;
          }
          updateSymptomsDisplay();
        });
      });

      collapseEl.appendChild(qEl);
      qContainer.appendChild(collapseEl);
    });

    // Recomputes visibility (CSS-transitioned via the .jtcm-sym-collapse--expanded class),
    // selection state, and the answered-count/complete-badge/Next-button without destroying any
    // question's DOM node — this is what makes the reveal/hide of follow-up questions animate
    // instead of popping instantly. Called on every option click instead of renderSymptoms().
    function updateSymptomsDisplay() {
      const freshAns = state.answers[prio] || {};
      const freshVisible = visibleQuestions(qs, freshAns);

      qs.forEach((q, qi) => {
        const wrap = qContainer.querySelector(`.jtcm-sym-collapse[data-qi="${qi}"]`);
        const isNowVisible = freshVisible.includes(q);
        wrap.classList.toggle('jtcm-sym-collapse--expanded', isNowVisible);
        wrap.inert = !isNowVisible;

        const currentAns = freshAns[qi];
        const selectedSet = Array.isArray(currentAns) ? currentAns : (typeof currentAns === 'number' ? [currentAns] : []);
        wrap.querySelectorAll('.sym-opt').forEach(btn => {
          const oi = +btn.dataset.oi;
          const isSel = selectedSet.includes(oi);
          btn.classList.toggle('sel', isSel);
          btn.querySelector('.jtcm-checkbox, .jtcm-radio').classList.toggle('sel', isSel);
        });
      });

      const freshAnswered = freshVisible.filter((q) => isAnswered(freshAns[qs.indexOf(q)])).length;
      el.querySelector('#sym-answered-line').textContent = `${freshAnswered}/${freshVisible.length} answered for ${cat ? cat.en : ''}`;
      el.querySelector('#sym-answered-footer').textContent = `${freshAnswered} of ${freshVisible.length} answered`;
      el.querySelector('#sym-complete-badge').style.display = freshAnswered === freshVisible.length ? '' : 'none';
      el.querySelector('#sym-next').disabled = !isStepComplete(qs, freshAns);
    }

    el.querySelector('#sym-next').addEventListener('click', goNext);
  }

  // ── Conditions (Step 4) ────────────────────────────────────
  function renderConditions() {
    const hiddenConds = GENDER_HIDDEN_CONDITIONS[state.profile.gender] || [];
    state.conditions = state.conditions.filter(id => !hiddenConds.includes(id));
    const visibleConditions = CONDITIONS.filter(c => !hiddenConds.includes(c.id));
    const el = containers.conditions;
    const NONE = 'none';

    el.innerHTML = `
      <div class="jtcm-eyebrow" style="margin-bottom:1rem">${eyebrowNum(5)} — Health background <span class="jtcm-eyebrow-line"></span> <span style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);letter-spacing:0;color:var(--jtcm-muted);text-transform:none">健康背景</span></div>
      <h2 class="jtcm-h2" style="color:var(--jtcm-ink);margin-bottom:.5rem">Anything we should <em style="color:var(--jtcm-sage-deep)">work around?</em></h2>
      <p style="color:var(--jtcm-muted);font-size:var(--jtcm-fs-body-sm);line-height:1.6;margin-bottom:2rem">Pick anything that applies — we'll quietly skip products that aren't recommended for your situation.</p>
      <div class="jtcm-grid-3" id="cond-grid"></div>
      <div id="cond-none-wrap" style="margin-top:.75rem"></div>
      <div style="margin-top:.75rem;font-size:var(--jtcm-fs-caption);color:var(--jtcm-muted);display:flex;align-items:flex-start;gap:.4rem">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" style="flex-shrink:0;margin-top:.1rem"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        General TCM safety practice. If you take prescription medication or have a chronic condition, consult your physician before starting any herbal product.
      </div>
      <div style="margin-top:2rem;padding-top:1.25rem;border-top:1px solid var(--jtcm-sage-soft);display:flex;align-items:center;justify-content:space-between">
        <div id="cond-count" style="font-size:var(--jtcm-fs-small);color:var(--jtcm-muted)"></div>
        <button id="cond-next" class="jtcm-btn jtcm-btn-sage" disabled>See my matches &#8594;</button>
      </div>`;

    function toggle(id) {
      if (id === NONE) {
        state.conditions = state.conditions.includes(NONE) ? [] : [NONE];
      } else {
        let next = state.conditions.filter(c => c !== NONE);
        next = next.includes(id) ? next.filter(c => c !== id) : [...next, id];
        state.conditions = next;
      }
      refreshConditions();
    }

    function refreshConditions() {
      document.querySelectorAll('.cond-btn').forEach(btn => {
        const sel = state.conditions.includes(btn.dataset.id);
        btn.classList.toggle('sel', sel);
        btn.querySelector('.jtcm-check').classList.toggle('sel', sel);
        btn.querySelector('.jtcm-check').textContent = sel ? '✓' : '+';
      });
      const real = state.conditions.filter(c => c !== NONE).length;
      const countEl = document.getElementById('cond-count');
      if (state.conditions.includes(NONE)) {
        countEl.innerHTML = 'Confirmed: <strong style="color:var(--jtcm-sage-deep)">no listed conditions</strong>';
      } else {
        countEl.innerHTML = `Selected: <strong style="color:var(--jtcm-sage-deep)">${real || 'none'}</strong>`;
      }
      document.getElementById('cond-next').disabled = state.conditions.length === 0;
    }

    // Condition cards (12, minus any gender-hidden for this session)
    const grid = el.querySelector('#cond-grid');
    visibleConditions.forEach(c => {
      const btn = document.createElement('button');
      btn.className = 'jtcm-card jtcm-card--condition cond-btn' + (state.conditions.includes(c.id) ? ' sel' : '');
      btn.dataset.id = c.id;
      btn.style.cssText = 'text-align:left;width:100%;display:flex;align-items:flex-start;justify-content:space-between;gap:.5rem';
      btn.innerHTML = `<div><div style="font-family:var(--jtcm-font-serif-text);font-size:1rem;line-height:1.25">${c.en}</div><div class="jtcm-card__meta" style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);margin-top:.2rem">${c.zh}</div></div><span class="jtcm-check ${state.conditions.includes(c.id) ? 'sel' : ''}">${state.conditions.includes(c.id) ? '✓' : '+'}</span>`;
      btn.addEventListener('click', () => toggle(c.id));
      grid.appendChild(btn);
    });

    // None of the above
    const noneBtn = document.createElement('button');
    noneBtn.className = 'jtcm-card jtcm-card--none-of-above cond-btn' + (state.conditions.includes(NONE) ? ' sel' : '');
    noneBtn.dataset.id = NONE;
    noneBtn.style.cssText = 'text-align:left;width:100%;display:flex;align-items:center;justify-content:space-between';
    noneBtn.innerHTML = `<div><div style="font-family:var(--jtcm-font-serif-text);font-size:1rem">None of the above</div><div class="jtcm-card__meta" style="font-family:'Noto Serif SC',serif;font-size:var(--jtcm-fs-small);margin-top:.2rem">以上皆无</div></div><span class="jtcm-check ${state.conditions.includes(NONE) ? 'sel' : ''}">${state.conditions.includes(NONE) ? '✓' : '+'}</span>`;
    noneBtn.addEventListener('click', () => toggle(NONE));
    el.querySelector('#cond-none-wrap').appendChild(noneBtn);

    refreshConditions();
    el.querySelector('#cond-next').addEventListener('click', goNext);
  }

  // ── Email gate ─────────────────────────────────────────────
  function showEmailGate() {
    emailGate.removeAttribute('hidden');
    document.getElementById('jtcm-gate-email').focus();
  }

  document.getElementById('jtcm-gate-cancel').addEventListener('click', () => {
    emailGate.setAttribute('hidden', '');
  });

  function subscribeToMarketing(email, marketing, done) {
    const form   = document.getElementById('jtcm-subscribe-form');
    const iframe = document.getElementById('jtcm-subscribe-frame');
    if (!form || !iframe) { done(); return; }

    let finished = false;
    const finish = () => { if (!finished) { finished = true; done(); } };
    const safetyTimeout = setTimeout(finish, 60000);

    const submitForm = () => {
      try {
        form.elements['contact[email]'].value = email;
        form.elements['customer[accepts_marketing]'].value = marketing ? 'true' : 'false';
        iframe.addEventListener('load', () => { clearTimeout(safetyTimeout); finish(); }, { once: true });
        form.submit();
      } catch (err) {
        console.error('[jintcm] marketing subscribe form-submit failed:', err);
        clearTimeout(safetyTimeout);
        finish();
      }
    };
    // Shopify's /contact endpoint requires a CAPTCHA token that only a real <form> submission
    // can attach — raw fetch() POSTs are rejected with "Missing CAPTCHA token" (400). The
    // documented fix is window.Shopify.captcha.protect(form, callback): it shows an interactive
    // challenge when hCaptcha decides one is needed, attaches the token once solved, then calls
    // back so we can fill the email and submit. The form posts into a hidden same-origin iframe
    // (no page navigation); we hold off on continuing until that completes — or a 60s safety
    // timeout — so the user has room to solve a challenge if one appears.
    // https://shopify.dev/docs/storefronts/themes/trust-security/captcha
    if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
      try {
        window.Shopify.captcha.protect(form, submitForm);
      } catch (err) {
        console.error('[jintcm] marketing subscribe captcha-protect failed:', err);
        clearTimeout(safetyTimeout);
        finish();
      }
    } else {
      submitForm();
    }
  }

  document.getElementById('jtcm-gate-submit').addEventListener('click', (e) => {
    const submitBtn = e.currentTarget;
    if (submitBtn.disabled) return; // guards against double-clicks during the async subscribe

    const emailEl   = document.getElementById('jtcm-gate-email');
    const errorEl   = document.getElementById('jtcm-gate-email-error');
    const valid     = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailEl.value.trim());
    if (!valid) { errorEl.style.display = 'block'; emailEl.focus(); return; }
    errorEl.style.display = 'none';

    const email     = emailEl.value.trim();
    const marketing = document.getElementById('jtcm-gate-marketing-consent').checked;

    saveState();
    submitBtn.disabled = true;
    // Email is always captured to Shopify (Customer record) regardless of the marketing
    // checkbox — only the accepts_marketing flag on that record reflects consent. See
    // BUILD_SPEC §8.1 / parking-lot item 15 for the compliance decision behind this.
    subscribeToMarketing(email, marketing, () => { window.location.href = RESULTS_URL; });
  });

  // ── Render dispatcher ──────────────────────────────────────
  function showStep(name) {
    Object.values(containers).forEach(c => c.setAttribute('hidden', ''));
    containers[name].removeAttribute('hidden');
  }

  function render() {
    const stepNames = ['intent', 'profile', 'priorities', 'symptoms', 'conditions'];
    const name      = stepNames[currentStep];
    showStep(name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (currentStep === 0) renderIntent();
    else if (currentStep === 1) renderProfile();
    else if (currentStep === 2) renderPriorities();
    else if (currentStep === 3) renderSymptoms();
    else renderConditions();
  }

  // ── Init ───────────────────────────────────────────────────
  if (AVAILABLE_CATEGORIES.length > 0) {
    // Fresh state every load: this is a shared kiosk-style QR entry point at
    // an event booth, not a returning-visitor flow — don't carry over a
    // previous visitor's answers.
    localStorage.removeItem(LS.intent);
    localStorage.removeItem(LS.profile);
    localStorage.removeItem(LS.priorities);
    localStorage.removeItem(LS.answers);
    localStorage.removeItem(LS.conditions);
    state.profile    = { gender: null, ageGroup: null };
    // Pre-select only when there's exactly one ready category — nothing meaningful to
    // choose. With 2+, the visitor picks which ones they want on the Priorities step.
    state.priorities = AVAILABLE_CATEGORIES.length === 1 ? [...AVAILABLE_CATEGORIES] : [];
    state.answers    = {};
    state.conditions = [];
    currentStep = 1; // skip Welcome/intent (step 0), start at Profile
  }
  updateProgress();
  render();
})();
}
